import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Send,
  Loader2,
  AlertTriangle,
  ShieldCheck,
  Sparkles,
  TestTube2,
  CheckCircle2,
  X as XIcon,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { RELEASES } from "@/data/releaseNotes";

const STEPS = { COMPOSE: "compose", CONFIRM: "confirm", RESULT: "result" };

function Badge({ color, children, testid }) {
  const palette = {
    teal: "border-[color:rgba(27,168,159,0.35)] bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]",
    warn: "border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.10)] text-[color:var(--accent-warm)]",
    muted: "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-3)]",
    danger: "border-[color:rgba(239,68,68,0.35)] bg-[color:rgba(239,68,68,0.10)] text-[color:var(--danger)]",
  }[color] || palette.muted;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold ${palette}`}>
      {children}
    </span>
  );
}

export default function SendCampaignDialog({ open, onOpenChange, sentReleaseIds = [], onSent }) {
  const [releaseId, setReleaseId] = useState("");
  const [subject, setSubject] = useState("");
  const [intro, setIntro] = useState("");
  const [closing, setClosing] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(STEPS.COMPOSE);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const releases = useMemo(() => {
    return RELEASES.map((r) => ({
      ...r,
      alreadySent: sentReleaseIds.includes(r.id),
    }));
  }, [sentReleaseIds]);

  // Default to the newest UNSENT release
  useEffect(() => {
    if (!open) return;
    if (!releaseId) {
      const firstUnsent = releases.find((r) => !r.alreadySent);
      const next = firstUnsent?.id || releases[0]?.id || "";
      setReleaseId(next);
    }
  }, [open, releases, releaseId]);

  const selectedRelease = useMemo(
    () => releases.find((r) => r.id === releaseId) || null,
    [releases, releaseId],
  );

  const defaultSubject = selectedRelease
    ? `What's new in Postmoro \u2014 ${selectedRelease.title}`
    : "";

  useEffect(() => {
    if (selectedRelease && !subject) setSubject(defaultSubject);
  }, [selectedRelease, subject, defaultSubject]);

  const runPreview = useCallback(async () => {
    if (!selectedRelease) return;
    setLoadingPreview(true);
    try {
      const res = await api.post("/admin/campaigns/preview", {
        release: selectedRelease,
        subject: subject || defaultSubject,
        intro,
        closing,
      });
      setPreview(res.data);
      setConfig(res.data.config);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not build preview.");
    } finally {
      setLoadingPreview(false);
    }
  }, [selectedRelease, subject, defaultSubject, intro, closing]);

  useEffect(() => {
    if (open && selectedRelease) runPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, releaseId]);

  const reset = (nextOpen) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setTimeout(() => {
        setReleaseId("");
        setSubject("");
        setIntro("");
        setClosing("");
        setPreview(null);
        setStep(STEPS.COMPOSE);
        setResult(null);
      }, 200);
    }
  };

  const doTestSend = async () => {
    if (!selectedRelease) return;
    setTesting(true);
    try {
      const res = await api.post("/admin/campaigns/test-send", {
        release: selectedRelease,
        subject: subject || defaultSubject,
        intro, closing,
        confirm_recipient_count: 0,
        is_test: true,
      });
      if (res.data?.mode === "mocked") {
        toast.success("Test send MOCKED in preview \u2014 no real email dispatched.");
      } else if (res.data?.ok) {
        toast.success(`Test email sent to ${res.data.to}`);
      } else {
        toast.error(`Test send failed: ${res.data?.message || "unknown"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed.");
    } finally {
      setTesting(false);
    }
  };

  const doRealSend = async () => {
    if (!selectedRelease || !preview) return;
    setSending(true);
    try {
      const res = await api.post("/admin/campaigns/send", {
        release: selectedRelease,
        subject: subject || defaultSubject,
        intro,
        closing,
        confirm_recipient_count: preview.recipients_estimate,
      });
      setResult(res.data);
      setStep(STEPS.RESULT);
      onSent?.();
      if (res.data?.status === "sent") toast.success("Campaign sent.");
      else if (res.data?.status === "partial_failure") toast.warning("Campaign partially sent \u2014 see result.");
      else toast.error("Campaign failed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  const alreadySent = selectedRelease?.alreadySent || preview?.already_sent;
  const canSend = preview && config?.ready && !alreadySent && (preview.recipients_estimate || 0) > 0;

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent
        className="sm:max-w-[720px] bg-[color:var(--bg-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] max-h-[90vh] overflow-y-auto"
        data-testid="send-campaign-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Send className="h-4 w-4 text-[color:var(--brand-primary)]" />
            {step === STEPS.COMPOSE && "Send Update Email"}
            {step === STEPS.CONFIRM && "Confirm broadcast"}
            {step === STEPS.RESULT && "Campaign result"}
          </DialogTitle>
        </DialogHeader>

        {step === STEPS.COMPOSE && (
          <div className="space-y-4 text-sm" data-testid="campaign-compose">
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">What's New release</label>
              <Select value={releaseId} onValueChange={setReleaseId}>
                <SelectTrigger data-testid="campaign-release-select" className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[color:var(--bg-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] max-h-[300px]">
                  {releases.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.date} \u2014 {r.title}{r.alreadySent ? " \u2022 already sent" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={defaultSubject}
                className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                data-testid="campaign-subject"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">Intro (optional)</label>
              <Textarea rows={2} value={intro} onChange={(e) => setIntro(e.target.value)} className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]" data-testid="campaign-intro" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">Closing (optional)</label>
              <Textarea rows={2} value={closing} onChange={(e) => setClosing(e.target.value)} className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]" data-testid="campaign-closing" />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={runPreview} disabled={loadingPreview} className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" data-testid="campaign-refresh-preview">
                {loadingPreview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Refresh preview
              </Button>
              <Button variant="outline" onClick={doTestSend} disabled={testing || !selectedRelease} className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" data-testid="campaign-test-send">
                {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TestTube2 className="h-4 w-4 mr-2" />}
                Send Test Email
              </Button>
            </div>

            {config && !config.ready ? (
              <div className="rounded-md border border-[color:rgba(239,68,68,0.35)] bg-[color:rgba(239,68,68,0.06)] p-3 text-xs" data-testid="campaign-config-blockers">
                <div className="flex items-center gap-1 font-semibold text-[color:var(--danger)]"><AlertTriangle className="h-3 w-3" /> Send blocked until configuration is added:</div>
                <ul className="mt-1 list-disc pl-5 text-[color:var(--text-2)]">
                  {config.blockers.map((b) => <li key={b}>{b}</li>)}
                </ul>
              </div>
            ) : null}

            {alreadySent ? (
              <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 text-xs text-[color:var(--text-2)]" data-testid="campaign-already-sent">
                This release was already sent{preview?.already_sent?.sent_at ? ` on ${new Date(preview.already_sent.sent_at).toLocaleString()}` : ""}. Deliberate resend requires a separate manual workflow.
              </div>
            ) : null}

            {preview ? (
              <div className="rounded-lg border border-[color:var(--border-subtle)] overflow-hidden" data-testid="campaign-preview">
                <div className="px-3 py-2 bg-[color:var(--surface-2)] text-xs text-[color:var(--text-3)] flex items-center gap-2 flex-wrap">
                  <Badge color="teal" testid="campaign-mode-badge">{config?.send_mode === "live" ? "LIVE MODE" : "MOCKED"}</Badge>
                  <span>From <span className="font-mono text-[color:var(--text-2)]">Postmoro &lt;{preview.from_email}&gt;</span></span>
                  <span className="ml-auto">Active subscribers: <span data-testid="campaign-recipients-count" className="font-semibold text-[color:var(--text-1)]">{preview.recipients_estimate}</span></span>
                </div>
                <iframe
                  title="campaign-preview-iframe"
                  data-testid="campaign-preview-iframe"
                  srcDoc={preview.html}
                  className="w-full h-[380px] bg-white"
                />
              </div>
            ) : null}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => reset(false)} className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" data-testid="campaign-cancel">Cancel</Button>
              <Button
                onClick={() => setStep(STEPS.CONFIRM)}
                disabled={!canSend}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
                data-testid="campaign-continue"
              >
                Continue \u2192
              </Button>
            </div>
          </div>
        )}

        {step === STEPS.CONFIRM && preview && (
          <div className="space-y-4 text-sm" data-testid="campaign-confirm">
            <div className="rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.08)] p-4">
              <div className="flex items-center gap-2 font-semibold text-[color:var(--accent-warm)]">
                <ShieldCheck className="h-4 w-4" />
                You&apos;re about to email {preview.recipients_estimate} active Postmoro subscribers.
              </div>
              <div className="mt-2 text-[color:var(--text-1)]"><span className="text-[color:var(--text-3)] text-xs">Subject:</span> {subject || defaultSubject}</div>
              <div className="mt-1 text-[color:var(--text-1)]"><span className="text-[color:var(--text-3)] text-xs">From:</span> Postmoro &lt;{preview.from_email}&gt;</div>
              <div className="mt-1 text-[color:var(--text-1)]"><span className="text-[color:var(--text-3)] text-xs">Release:</span> {selectedRelease?.title} ({selectedRelease?.id})</div>
              <div className="mt-1 text-[color:var(--text-1)]"><span className="text-[color:var(--text-3)] text-xs">Mode:</span> {config?.send_mode?.toUpperCase()}</div>
              <div className="mt-3 text-xs text-[color:var(--text-2)]">This action cannot be undone. Preview environments run in MOCKED mode and will not deliver real email.</div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStep(STEPS.COMPOSE)} className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" data-testid="campaign-back">Back</Button>
              <Button
                onClick={doRealSend}
                disabled={sending}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="campaign-confirm-send"
              >
                {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Send to {preview.recipients_estimate} subscribers
              </Button>
            </div>
          </div>
        )}

        {step === STEPS.RESULT && result && (
          <div className="space-y-3 text-sm" data-testid="campaign-result">
            <div className={"rounded-md p-3 border " + (result.status === "sent" ? "border-[color:rgba(34,197,94,0.30)] bg-[color:rgba(34,197,94,0.06)]" : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]")}>
              <div className="flex items-center gap-2 font-semibold">
                {result.status === "sent" ? <CheckCircle2 className="h-4 w-4 text-[color:var(--success)]" /> : <AlertTriangle className="h-4 w-4 text-[color:var(--accent-warm)]" />}
                Status: {result.status.replace("_", " ")}
              </div>
              <div className="mt-1 text-[color:var(--text-2)]">Recipients: {result.recipient_count}</div>
              <div className="text-[color:var(--text-2)]">Sent: {result.success_count}</div>
              <div className="text-[color:var(--text-2)]">Failed: {result.failure_count}</div>
              <div className="text-[color:var(--text-3)] text-xs mt-1">Mode: {result.send_mode}</div>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => reset(false)} className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]" data-testid="campaign-close-result">Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
